/**
 * The Sunday-evening report, computed — never fetched. Every input is data
 * the phone already has synced; this is a pure reduction over it, which is
 * what lets it run identically for this week and for a week revisited months
 * later out of the archive.
 *
 * Takes one options object rather than a row of positional arguments: there
 * are nine inputs now, several of them nullable numbers, and a call site that
 * silently swapped the kcal target for the protein one would produce a report
 * that is wrong rather than one that fails to compile.
 */

import {
  type AwardKind,
  addDays,
  classAside,
  classifyFoodName,
  type FoodComedyClass,
  foldLt,
  hashSeed,
  isDrinkOrWaterName,
  mulberry32,
} from '@gramas/shared'
import type { LocalAward, LocalBodyMeasurement, LocalLogEntry, LocalWorkout } from '../db/dexie.js'
import { COMMENTARY, SIGN_OFFS } from './copy.js'

export type WrappedVerdict = 'on' | 'over' | 'under' | 'plain'

export interface TopFood {
  name: string
  count: number
  foodClass: FoodComedyClass | null
  aside: string
}

export interface WrappedAward {
  key: string
  kind: AwardKind
}

/**
 * One column of the week's chart. All seven are always present, in order,
 * including the days nothing was logged — a bar chart that quietly omits the
 * empty days is a chart that flatters, and the empty ones are half of what the
 * report is about.
 */
export interface DayBar {
  date: string
  kcal: number
  logged: boolean
}

/** One day worth singling out, with the figure that made it stand out. */
export interface DayHighlight {
  date: string
  kcal: number
  /** kcal − target, signed. Null only when there was no target that week. */
  gapKcal: number | null
}

export interface WrappedProtein {
  avgG: number
  targetG: number | null
  /** Days that reached the protein target; 0 when there was no target. */
  daysMet: number
}

export interface WrappedWeek {
  weekStart: string
  weekEnd: string
  daysLogged: number
  avgKcal: number
  totalKcal: number
  /** Monday to Sunday, always seven, for the week's chart. */
  days: readonly DayBar[]
  /** The target the week was measured against, for the line across the chart. */
  kcalTarget: number | null
  verdict: WrappedVerdict
  /** avgKcal − target, signed; null when there is no target to compare against. */
  gapKcal: number | null
  weightChangeKg: number | null
  workoutCount: number
  topFood: TopFood | null
  bestDay: DayHighlight | null
  worstDay: DayHighlight | null
  protein: WrappedProtein
  /** Longest run of consecutive logged days inside the week. */
  longestRun: number
  awards: WrappedAward[]
  /** Her read on the week as a whole — stable per week per account. */
  commentary: string
  /** Stable per week per account — the same week always reads the same. */
  signOff: string
}

export interface WrappedInput {
  weekStart: string
  weekEnd: string
  entries: readonly LocalLogEntry[]
  kcalTarget: number | null
  proteinTargetG: number | null
  weights: readonly LocalBodyMeasurement[]
  workouts: readonly LocalWorkout[]
  awards: readonly LocalAward[]
  userId: string
}

const ON_TARGET_BAND_KCAL = 50

/** Deterministic pick — same account, same week, same line. */
function seededPick(pool: readonly string[], seed: string): string {
  const roll = mulberry32(hashSeed(seed))()
  return pool[Math.floor(roll * pool.length) % pool.length]!
}

function verdictOf(avgKcal: number, target: number | null): WrappedVerdict {
  if (target == null) return 'plain'
  const gap = avgKcal - target
  if (Math.abs(gap) <= ON_TARGET_BAND_KCAL) return 'on'
  return gap > 0 ? 'over' : 'under'
}

/** The longest run of consecutive dates present in a set of logged days. */
function longestRunOf(dates: readonly string[]): number {
  const sorted = [...dates].sort()
  const dayOf = (date: string): number => Date.parse(`${date}T00:00:00Z`) / 86_400_000
  let best = 0
  let run = 0
  let previous: number | null = null
  for (const date of sorted) {
    const day = dayOf(date)
    run = previous != null && day - previous === 1 ? run + 1 : 1
    previous = day
    if (run > best) best = run
  }
  return best
}

export function buildWrapped(input: WrappedInput): WrappedWeek {
  const { weekStart, weekEnd, kcalTarget, proteinTargetG, userId } = input
  const live = input.entries.filter((e) => e.deletedAt == null)

  const kcalByDate = new Map<string, number>()
  const proteinByDate = new Map<string, number>()
  for (const e of live) {
    kcalByDate.set(e.logDate, (kcalByDate.get(e.logDate) ?? 0) + e.kcal)
    proteinByDate.set(e.logDate, (proteinByDate.get(e.logDate) ?? 0) + e.protein)
  }
  const loggedDates = new Set(
    [...kcalByDate.entries()].filter(([, kcal]) => kcal > 0).map(([date]) => date),
  )
  const daysLogged = loggedDates.size
  const totalKcal = live.reduce((sum, e) => sum + e.kcal, 0)
  const avgKcal = daysLogged > 0 ? Math.round(totalKcal / daysLogged) : 0

  const counts = new Map<string, { name: string; count: number }>()
  for (const e of live) {
    if (isDrinkOrWaterName(e.snapshotName)) continue
    const key = foldLt(e.snapshotName.trim())
    if (key === '') continue
    const bucket = counts.get(key)
    if (bucket == null) counts.set(key, { name: e.snapshotName, count: 1 })
    else bucket.count += 1
  }
  let topFood: TopFood | null = null
  for (const { name, count } of counts.values()) {
    if (count < 2) continue
    if (topFood == null || count > topFood.count) {
      const foodClass = classifyFoodName(name)
      topFood = { name, count, foodClass, aside: classAside(foodClass, count) }
    }
  }

  // Best and worst need something to be best and worst *against*: without a
  // target the lowest-kcal day is not virtuous, it is just the lowest, and
  // saying otherwise would be the report inventing an opinion it cannot hold.
  let bestDay: DayHighlight | null = null
  let worstDay: DayHighlight | null = null
  if (kcalTarget != null && daysLogged >= 2) {
    let closest: { date: string; kcal: number; gap: number } | null = null
    let highest: { date: string; kcal: number; gap: number } | null = null
    for (const [date, kcal] of kcalByDate) {
      if (!loggedDates.has(date)) continue
      const day = { date, kcal: Math.round(kcal), gap: Math.round(kcal - kcalTarget) }
      if (closest == null || Math.abs(day.gap) < Math.abs(closest.gap)) closest = day
      if (highest == null || day.gap > highest.gap) highest = day
    }
    if (closest != null) bestDay = { date: closest.date, kcal: closest.kcal, gapKcal: closest.gap }
    // A week whose closest day is also its highest has one day worth naming,
    // not two dressed up as a comparison.
    if (highest != null && highest.date !== closest?.date) {
      worstDay = { date: highest.date, kcal: highest.kcal, gapKcal: highest.gap }
    }
  }

  const proteinTotal = live.reduce((sum, e) => sum + e.protein, 0)
  const protein: WrappedProtein = {
    avgG: daysLogged > 0 ? Math.round(proteinTotal / daysLogged) : 0,
    targetG: proteinTargetG,
    daysMet:
      proteinTargetG == null
        ? 0
        : [...proteinByDate.values()].filter((g) => g >= proteinTargetG).length,
  }

  const liveWeights = input.weights
    .filter((w) => w.deletedAt == null && w.weightKg != null)
    .sort((a, b) => a.measuredOn.localeCompare(b.measuredOn))
  const firstWeight = liveWeights[0]?.weightKg ?? null
  const lastWeight = liveWeights.at(-1)?.weightKg ?? null
  const weightChangeKg =
    firstWeight != null && lastWeight != null && liveWeights.length >= 2
      ? Math.round((lastWeight - firstWeight) * 10) / 10
      : null

  const liveAwards = input.awards.filter(
    (a) => a.deletedAt == null && a.awardedOn >= weekStart && a.awardedOn <= weekEnd,
  )

  const days: DayBar[] = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i)
    const kcal = kcalByDate.get(date)
    return { date, kcal: Math.round(kcal ?? 0), logged: (kcal ?? 0) > 0 }
  })

  const verdict = verdictOf(avgKcal, kcalTarget)
  return {
    weekStart,
    weekEnd,
    daysLogged,
    avgKcal,
    totalKcal: Math.round(totalKcal),
    days,
    kcalTarget,
    verdict,
    gapKcal: kcalTarget == null ? null : Math.round(avgKcal - kcalTarget),
    weightChangeKg,
    workoutCount: input.workouts.filter((w) => w.deletedAt == null).length,
    topFood,
    bestDay,
    worstDay,
    protein,
    longestRun: longestRunOf([...loggedDates]),
    awards: liveAwards.map((a) => ({ key: a.awardKey, kind: a.kind })),
    commentary: seededPick(COMMENTARY[verdict], `wrapped-note:${userId}:${weekStart}`),
    signOff: seededPick(SIGN_OFFS, `wrapped:${userId}:${weekStart}`),
  }
}
